'use client';

import { cn } from '@core/utils';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

interface CountdownTimerProps {
  /**
   * 카운트다운 종료 날짜 (ISO 문자열 또는 Date 객체)
   */
  endDate: string | Date;
  /**
   * 라벨 텍스트 (기본값: "마감까지")
   */
  label?: string;
  /**
   * 카운트다운 완료 시 콜백
   */
  onComplete?: () => void;
  /**
   * 컴포넌트 추가 클래스명
   */
  className?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
  total: number;
}

const SpeechBubbleLabel = memo(({ label }: { label: string }) => (
  <div className="relative flex h-30 cursor-default items-center justify-center rounded-r-medium rounded-bl-medium rounded-tl-none bg-R-500 px-14 py-16 transition-transform duration-200 hover:scale-105">
    <span className="whitespace-nowrap font-700 text-Gray-white typo-body4">
      {label}
    </span>
  </div>
));

SpeechBubbleLabel.displayName = 'SpeechBubbleLabel';

const TimerDigits = memo(({ timeLeft }: { timeLeft: TimeLeft }) => {
  const formatTime = useCallback((time: number) => {
    return time.toString().padStart(2, '0');
  }, []);

  return (
    <div className="font-500 tabular-nums text-Gray-white typo-body3">
      <span>{formatTime(timeLeft.days)}</span>
      <span>일 </span>
      <span>{formatTime(timeLeft.hours)}</span>
      <span>시간 </span>
      <span>{formatTime(timeLeft.minutes)}</span>
      <span>분 </span>
      <span>{formatTime(timeLeft.seconds)}</span>
      <span>초 </span>
      <span>{formatTime(timeLeft.milliseconds)}</span>
    </div>
  );
});

TimerDigits.displayName = 'TimerDigits';

const CountdownTimerComponent = ({
  endDate,
  label = '마감까지',
  onComplete,
  className,
}: CountdownTimerProps) => {
  const [, forceUpdate] = useState({});
  const isCompleteRef = useRef(false);
  const isClient = typeof window !== 'undefined';

  const calculateTimeLeft = useCallback((): TimeLeft => {
    const targetDate = dayjs(endDate);
    const now = dayjs();
    const difference = targetDate.diff(now);

    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
        total: 0,
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      milliseconds: Math.floor((difference % 1000) / 10),
      total: difference,
    };
  }, [endDate]);

  useEffect(() => {
    let animationFrame: number;

    const updateTimer = () => {
      const timeLeft = calculateTimeLeft();

      if (timeLeft.total <= 0 && !isCompleteRef.current) {
        isCompleteRef.current = true;
        onComplete?.();
      }

      if (timeLeft.total > 0) {
        animationFrame = requestAnimationFrame(updateTimer);
      }

      forceUpdate({});
    };

    animationFrame = requestAnimationFrame(updateTimer);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [calculateTimeLeft, onComplete]);

  if (!isClient) {
    return (
      <div
        className={cn(
          'gap-10 bg-Gray-black/60 px-0 py-12 backdrop-blur-md flex-center',
          className,
        )}
      >
        <div className="relative flex h-30 cursor-default items-center justify-center rounded-r-medium rounded-bl-medium rounded-tl-none bg-R-500 px-14 py-16 transition-transform duration-200 hover:scale-105">
          <span className="whitespace-nowrap font-700 text-Gray-white typo-body4">
            {label}
          </span>
        </div>
        <div className="font-500 tabular-nums text-Gray-white opacity-0 typo-body3">
          00일 00시간 00분 00초 00
        </div>
      </div>
    );
  }

  const timeLeft = calculateTimeLeft();

  if (timeLeft.total <= 0) {
    return (
      <div
        className={cn(
          'gap-10 bg-Gray-black/60 px-0 py-12 backdrop-blur-md flex-center',
          className,
        )}
      >
        <div className="flex h-30 items-center justify-center rounded-medium bg-Gray-550 px-14 py-16">
          <span className="whitespace-nowrap font-700 text-Gray-white typo-body4">
            마감
          </span>
        </div>
        <div className="font-500 text-Gray-white typo-body3">
          이벤트가 종료되었습니다
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'gap-10 bg-Gray-black/60 px-0 py-12 backdrop-blur-md flex-center',
        className,
      )}
    >
      <SpeechBubbleLabel label={label} />

      <TimerDigits timeLeft={timeLeft} />
    </div>
  );
};

export const CountdownTimer = memo(CountdownTimerComponent);
